/**
 * AchievementSystem — runtime state and unlock detection for achievements.
 *
 * Responsibilities:
 *  - Owns the live PersistentStats blob.
 *  - Holds `unlocked[id] = tierIndex+1` (0 = locked, N = N tiers unlocked).
 *  - Exposes `recheck()` which re-evaluates all defs against current stats
 *    and pushes toast notifications when new tiers unlock.
 *  - Serializes/deserializes stats + unlocks into the save envelope.
 *
 * Design notes:
 *  - No central event bus in this codebase. Hook sites mutate `stats` directly
 *    and then call `recheck()` (cheap: O(110) simple comparisons).
 *  - `recheck()` can be called very frequently; the work is a single loop of
 *    primitive comparisons so there's no need for per-key dispatch tables.
 *  - Toast queue is a plain array; the UI overlay drains from the front.
 */

import {
  ACHIEVEMENTS,
  type AchievementDef,
  type AchievementCategory,
} from './AchievementData';
import {
  defaultPersistentStats,
  serializeStats,
  deserializeStats,
  type PersistentStats,
} from './PersistentStats';

export interface ToastSpec {
  id: string;
  name: string;
  tierIndex: number;                 // 0-based
  tierName?: string | undefined;
  description: string;
  createdMs: number;
}

export interface AchievementSave {
  unlocked: Record<string, number>;  // id → tiers unlocked count
}

export class AchievementSystem {
  stats: PersistentStats = defaultPersistentStats();
  /** id → number of tiers unlocked so far (0 means none). */
  unlocked: Record<string, number> = {};
  /** FIFO queue of pending unlock toasts. UI drains this. */
  pendingToasts: ToastSpec[] = [];

  /** Transient context for progression/exploration achievements whose values
   *  come from live systems rather than PersistentStats. Phase 3 populates
   *  this; before then, fields default to 0. */
  private ctx: Record<string, number> = {};

  constructor() {
    for (const def of ACHIEVEMENTS) this.unlocked[def.id] = 0;
  }

  /** Update a derived-context value (e.g. current level, stat sum) that feeds
   *  into getValue via the `_`-prefixed shim fields on the stats object. */
  setContext(key: string, value: number) {
    this.ctx[key] = value;
  }

  /** Returns a stats snapshot with context fields merged in. The shim fields
   *  are read by the `_`-prefixed lookups in AchievementData. */
  private statsWithContext(): PersistentStats {
    // Mutating the live stats would be faster but leaks context into saves;
    // instead we return a lightweight proxy via Object.assign onto a shallow
    // clone only when context is non-empty.
    if (Object.keys(this.ctx).length === 0) return this.stats;
    return Object.assign({}, this.stats, this.ctx) as PersistentStats;
  }

  /** Re-evaluate every achievement against current stats. Cheap (~110 compares).
   *  Call after any stat mutation (or at run end / context update). */
  recheck(): void {
    const s = this.statsWithContext();
    const now = performance.now();
    for (const def of ACHIEVEMENTS) {
      const value = def.getValue(s);
      const already = this.unlocked[def.id] ?? 0;
      const target = tiersUnlockedFor(def, value);
      if (target > already) {
        for (let t = already; t < target; t++) {
          this.pendingToasts.push({
            id: def.id,
            name: def.name,
            tierIndex: t,
            tierName: def.tierNames?.[t],
            description: formatDescription(def, t),
            createdMs: now,
          });
        }
        this.unlocked[def.id] = target;
      }
    }
  }

  /** Total tier-weighted unlocked count (for Completionist meter). */
  totalUnlockedTiers(): number {
    let n = 0;
    for (const id of Object.keys(this.unlocked)) n += this.unlocked[id] ?? 0;
    return n;
  }

  totalAvailableTiers(): number {
    let n = 0;
    for (const def of ACHIEVEMENTS) n += def.tiers.length;
    return n;
  }

  /** All unlocked? — useful for meta achievements. */
  isCategoryComplete(category: AchievementCategory): boolean {
    for (const def of ACHIEVEMENTS) {
      if (def.category !== category) continue;
      if ((this.unlocked[def.id] ?? 0) < def.tiers.length) return false;
    }
    return true;
  }

  serialize(): { stats: ReturnType<typeof serializeStats>; achievements: AchievementSave } {
    return {
      stats: serializeStats(this.stats),
      achievements: { unlocked: { ...this.unlocked } },
    };
  }

  deserializeStats(raw: unknown) {
    this.stats = deserializeStats(raw);
  }

  deserializeUnlocks(raw: unknown) {
    if (!raw || typeof raw !== 'object') return;
    const o = raw as { unlocked?: unknown };
    if (!o.unlocked || typeof o.unlocked !== 'object') return;
    const u = o.unlocked as Record<string, unknown>;
    for (const def of ACHIEVEMENTS) {
      const v = u[def.id];
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
        this.unlocked[def.id] = Math.min(Math.floor(v), def.tiers.length);
      }
    }
  }
}

function tiersUnlockedFor(def: AchievementDef, value: number): number {
  if (def.minTracker) {
    // Lower-is-better: an unset value (0) means not-yet-tracked, so nothing unlocks.
    if (value <= 0) return 0;
    // Tiers are ascending thresholds, but lower value unlocks HIGHER tier.
    // e.g. tiers [480, 300, 180] — value=250s unlocks tier 2 (≤300) but not tier 3 (≤180).
    let count = 0;
    for (const t of def.tiers) {
      if (value <= t) count++;
    }
    return count;
  }
  // Standard: higher value unlocks more tiers.
  let count = 0;
  for (const t of def.tiers) {
    if (value >= t) count++;
    else break;
  }
  return count;
}

function formatDescription(def: AchievementDef, tierIndex: number): string {
  const threshold = def.tiers[tierIndex] ?? def.tiers[def.tiers.length - 1]!;
  const formatted = def.formatValue ? def.formatValue(threshold) : String(threshold);
  return def.description.replace(/\{X\}/g, formatted);
}
